/**
 * The checks the backend runs when inquiry emails are saved, run while
 * typing so Save can say what is wrong instead of failing with a 422.
 */

/** The only {{variables}} a template may use, in either language
 *  (INQUIRY_TEMPLATE_VARS; anything else is INVALID_TEMPLATE_VARIABLE). */
const KNOWN_VARIABLES = new Set([
  "{{supplier_name}}",
  "{{lieferant}}",
  "{{deadline}}",
  "{{frist}}",
  "{{reference}}",
  "{{referenz}}",
  "{{regulations}}",
  "{{regelwerke}}",
])
const VARIABLE = /\{\{[^}]*\}\}/g

/** The reply-threading token the backend adds to the subject itself
 *  (RESERVED_REFERENCE_TOKEN). */
const REFERENCE_TOKEN = /\[Ref:\s*[^\]]+?\s*\]/i

/** How the backend checks a Cc address (INVALID_DEFAULT_CC): separators and whitespace
 *  around it are cleaned off first (`clean_email`), none may remain inside. */
const EMAIL = /^[^\s@;,]+@[^\s@;,]+\.[^\s@;,]+$/
const EDGE_SEPARATORS = /^[\s;,]+|[\s;,]+$/g

/** A brace run as a whole ("{{referenz}}}" stays one candidate, so a brace too many
 *  cannot hide behind a known token), or one of our variable names with a brace on one
 *  side only. Same rule as the backend's stray_placeholders. */
const NAMES = [...KNOWN_VARIABLES].map((token) => token.slice(2, -2))
const CANDIDATE = new RegExp(
  `\\{+[^{}\\n]*\\}+|\\{+\\s*(?:${NAMES.join("|")})\\b|\\b(?:${NAMES.join("|")})\\s*\\}+`,
  "gi",
)

/** Anything but an exact known {{variable}} that is doubly braced or names one of ours:
 *  sent verbatim. Other braces are text the sender meant ("Pb {total}"). */
export function strayPlaceholders(texts: string[]): string[] {
  const found = texts.flatMap((text) =>
    (text.match(CANDIDATE) ?? []).filter((token) => {
      if (KNOWN_VARIABLES.has(token)) return false
      const inner = token.replace(/^[{\s]+|[}\s]+$/g, "").toLowerCase()
      return (
        token.startsWith("{{") || token.endsWith("}}") || NAMES.includes(inner)
      )
    }),
  )
  return [...new Set(found)]
}

/** The {{variables}} in these texts that the backend would refuse, broken ones included. */
export function unknownVariables(texts: string[]): string[] {
  const used = texts.flatMap((text) => text.match(VARIABLE) ?? [])
  return [
    ...new Set([
      ...used.filter((token) => !KNOWN_VARIABLES.has(token)),
      ...strayPlaceholders(texts),
    ]),
  ]
}

/** Whether a text names the asked regulations ({{regulations}} or {{regelwerke}}): the
 *  shipped templates do, an org's own may not yet (it keeps its text, T2 doc D3). */
export function namesRegulations(text: string): boolean {
  return /\{\{(regulations|regelwerke)\}\}/.test(text)
}

/** Whether a text uses the deadline ({{deadline}} or {{frist}}). In the first-mail subject
 *  it goes stale: reminders repeat that subject ("Re: …") after the deadline moved. */
export function namesDeadline(text: string): boolean {
  return /\{\{(deadline|frist)\}\}/.test(text)
}

export function containsReferenceToken(texts: string[]): boolean {
  return texts.some((text) => REFERENCE_TOKEN.test(text))
}

export function invalidAddresses(addresses: string[]): string[] {
  return addresses.filter((address) => {
    const cleaned = address.replace(EDGE_SEPARATORS, "")
    return cleaned !== "" && !EMAIL.test(cleaned)
  })
}
